/**
 * =============================================================================
 * IDS CRM Web - Asset Create/Edit Form
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import { useAuth } from '../context/AuthContext';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import CustomerSearchSelect from '../components/CustomerSearchSelect';
import { DEFAULT_ASSET_CONFIG, getActiveOptions, getAssetCreatableOptions, getAssetTypeMeta, normaliseAssetConfig, resolveAssetImageSrc } from '../utils/assetTypes';
import './AssetForm.css';

function AssetForm() {
  const { guardElement, markDirty } = useUnsavedChanges();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { authFetch } = useAuth();
  const isEdit = Boolean(id);
  const initialCustomerId = searchParams.get('customer_id') || searchParams.get('customer') || '';
  const initialFarmSiteId = searchParams.get('farm_site_id') || searchParams.get('site_id') || '';
  const initialRobotType = searchParams.get('robot_type') || 'pre_brush';
  const initialLinkedOpportunityId = searchParams.get('linked_opportunity_id') || '';

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Phase 9: assets minted at won sit in pending_install with serial / site /
  // install date still unknown — the form allows saving them incomplete
  // (progressive completion); choosing another status = activation, which the
  // server validates for completeness.
  const [loadedStatus, setLoadedStatus] = useState(null);
  const [staleRecord, setStaleRecord] = useState(false);
  const [customerLabel, setCustomerLabel] = useState('');
  const [farmSites, setFarmSites] = useState([]);
  const [assetConfig, setAssetConfig] = useState(DEFAULT_ASSET_CONFIG);
  const [expandedSections, setExpandedSections] = useState({
    equipment: false,
    software: false,
    hardware: false,
    quality: false
  });

  const [formData, setFormData] = useState({
    customer_id: '',
    farm_site_id: '',
    serial_number: '',
    quote_number: '',
    robot_type: initialRobotType,
    linked_opportunity_id: initialLinkedOpportunityId,
    build_date: '',
    install_date: '',
    warranty_expiry_date: '',
    contract_months_override: '',
    brush_life_cleans: '60000',
    spray_chemical: '',
    spray_dilution_ratio: '',
    remote_access_id: '',
    general_project_description: '',
    invoicing_type: 'single',
    invoice_amount: '',
    invoice_milestones: [],
    status: 'active',
    health_flag: 'green',
    install_notes: '',
    // Cabinet serials
    control_cabinet_serial: '',
    power_cabinet_serial: '',
    pump_cabinet_serial_1: '',
    pump_cabinet_serial_2: '',
    // KUKA robot 1
    robot_serial_1: '',
    krc_cabinet_serial_1: '',
    kpp_serial_1: '',
    ksp_serial_1: '',
    pc_serial_1: '',
    robot_wrist_serial_1: '',
    resolver_serial_1: '',
    smartpad_serial_1: '',
    // KUKA robot 2 (dual)
    robot_serial_2: '',
    krc_cabinet_serial_2: '',
    kpp_serial_2: '',
    ksp_serial_2: '',
    pc_serial_2: '',
    robot_wrist_serial_2: '',
    resolver_serial_2: '',
    smartpad_serial_2: '',
    adapter2_serial: '',
    // Beckoff parts
    input_module_serial_1: '',
    input_module_serial_2: '',
    safety_module_serial: '',
    output_module_serial: '',
    ej_coupler_serial: '',
    ptl_pcb_serial: '',
    beckoff_plc_serial: '',
    beckoff_psu_2420_serial: '',
    beckoff_psu_4810_serial: '',
    beckoff_os_type: '',
    // Pump cabinet
    flojet_pump_serial: '',
    // Software versions
    kss_version_1: '',
    kss_version_2: '',
    plc_version: '',
    tool_camera_version: '',
    // Cow locate
    cow_locate_camera_enabled: false,
    cow_locate_mac_id: '',
    // Hardware config
    pcb_type: '',
    pin_type: '',
    air_motor_type: '',
    air_motor_serial_1: '',
    air_motor_serial_2: '',
    air_regulator_revision: '',
    pressure_relief_installed: false,
    // Network hardware
    unifi_router_size: '',
    unifi_router_mac: '',
    tsw_switch_serial: '',
    // Tracking
    waybill_number: '',
    deviation_notes: '',
    // Maintenance durations (in hours)
    annual_service_duration_hours: '4',
    health_check_duration_hours: '1.5'
  });

  const robotTypes = getAssetCreatableOptions(assetConfig.asset_types);
  const statusTypes = getActiveOptions(assetConfig.statuses).filter(option => option.value);
  const healthTypes = getActiveOptions(assetConfig.health_flags);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const fetchFarmSites = useCallback(async (customerId, preferredSiteId = '') => {
    if (!customerId) {
      setFarmSites([]);
      setFormData(prev => ({ ...prev, farm_site_id: '' }));
      return;
    }

    try {
      const response = await authFetch(`/api/v1/customers/${customerId}/sites`);
      if (!response.ok) return;
      const data = await response.json();
      const sites = data.data || [];
      setFarmSites(sites);

      const preferredSite = sites.find(site => site.site_id === preferredSiteId);
      const primarySite = sites.find(site => site.is_primary);
      setFormData(prev => ({
        ...prev,
        farm_site_id: preferredSite?.site_id || prev.farm_site_id || primarySite?.site_id || sites[0]?.site_id || ''
      }));
    } catch (err) {
      console.error('Farm site load failed:', err);
    }
  }, [authFetch]);

  useEffect(() => {
    if (isEdit) {
      const fetchAsset = async () => {
        try {
          const response = await authFetch(`/api/v1/assets/${id}`);
          if (response.ok) {
            const data = await response.json();
            setLoadedStatus(data.status || null);
            setFormData({
              customer_id: data.customer_id || '',
              farm_site_id: data.farm_site_id || '',
              serial_number: data.serial_number || '',
              quote_number: data.quote_number || '',
              robot_type: data.robot_type || 'pre_brush',
              build_date: data.build_date ? data.build_date.split('T')[0] : '',
              install_date: data.install_date ? data.install_date.split('T')[0] : '',
              contract_months_override: data.contract_months_override?.toString() || '',
              warranty_expiry_date: data.warranty_expiry_date ? data.warranty_expiry_date.split('T')[0] : '',
              brush_life_cleans: data.brush_life_cleans?.toString() || '60000',
              spray_chemical: data.spray_chemical || '',
              spray_dilution_ratio: data.spray_dilution_ratio || '',
              remote_access_id: data.remote_access_id || '',
              general_project_description: data.general_project_description || '',
              invoicing_type: data.invoicing_type || 'single',
              invoice_amount: data.invoice_amount_minor_units ? String((Number(data.invoice_amount_minor_units) / 100).toFixed(2)) : '',
              invoice_milestones: Array.isArray(data.invoice_milestones)
                ? data.invoice_milestones.map(milestone => ({
                  name: milestone.name || '',
                  amount: milestone.amount_minor_units ? String((Number(milestone.amount_minor_units) / 100).toFixed(2)) : ''
                }))
                : [],
              status: data.status || 'active',
              health_flag: data.health_flag || 'green',
              install_notes: data.install_notes || '',
              control_cabinet_serial: data.control_cabinet_serial || '',
              power_cabinet_serial: data.power_cabinet_serial || '',
              pump_cabinet_serial_1: data.pump_cabinet_serial_1 || '',
              pump_cabinet_serial_2: data.pump_cabinet_serial_2 || '',
              robot_serial_1: data.robot_serial_1 || '',
              krc_cabinet_serial_1: data.krc_cabinet_serial_1 || '',
              kpp_serial_1: data.kpp_serial_1 || '',
              ksp_serial_1: data.ksp_serial_1 || '',
              pc_serial_1: data.pc_serial_1 || '',
              robot_wrist_serial_1: data.robot_wrist_serial_1 || '',
              resolver_serial_1: data.resolver_serial_1 || '',
              smartpad_serial_1: data.smartpad_serial_1 || '',
              robot_serial_2: data.robot_serial_2 || '',
              krc_cabinet_serial_2: data.krc_cabinet_serial_2 || '',
              kpp_serial_2: data.kpp_serial_2 || '',
              ksp_serial_2: data.ksp_serial_2 || '',
              pc_serial_2: data.pc_serial_2 || '',
              robot_wrist_serial_2: data.robot_wrist_serial_2 || '',
              resolver_serial_2: data.resolver_serial_2 || '',
              smartpad_serial_2: data.smartpad_serial_2 || '',
              adapter2_serial: data.adapter2_serial || '',
              input_module_serial_1: data.input_module_serial_1 || '',
              input_module_serial_2: data.input_module_serial_2 || '',
              safety_module_serial: data.safety_module_serial || '',
              output_module_serial: data.output_module_serial || '',
              ej_coupler_serial: data.ej_coupler_serial || '',
              ptl_pcb_serial: data.ptl_pcb_serial || '',
              beckoff_plc_serial: data.beckoff_plc_serial || '',
              beckoff_psu_2420_serial: data.beckoff_psu_2420_serial || '',
              beckoff_psu_4810_serial: data.beckoff_psu_4810_serial || '',
              beckoff_os_type: data.beckoff_os_type || '',
              flojet_pump_serial: data.flojet_pump_serial || '',
              kss_version_1: data.kss_version_1 || '',
              kss_version_2: data.kss_version_2 || '',
              plc_version: data.plc_version || '',
              tool_camera_version: data.tool_camera_version || '',
              cow_locate_camera_enabled: data.cow_locate_camera_enabled || false,
              cow_locate_mac_id: data.cow_locate_mac_id || '',
              pcb_type: data.pcb_type || '',
              pin_type: data.pin_type || '',
              air_motor_type: data.air_motor_type || '',
              air_motor_serial_1: data.air_motor_serial_1 || '',
              air_motor_serial_2: data.air_motor_serial_2 || '',
              air_regulator_revision: data.air_regulator_revision || '',
              pressure_relief_installed: data.pressure_relief_installed || false,
              unifi_router_size: data.unifi_router_size || '',
              unifi_router_mac: data.unifi_router_mac || '',
              tsw_switch_serial: data.tsw_switch_serial || '',
              waybill_number: data.waybill_number || '',
              deviation_notes: data.deviation_notes || '',
              annual_service_duration_hours: data.annual_service_duration_hours?.toString() || '4',
              health_check_duration_hours: data.health_check_duration_hours?.toString() || '1.5',
              updated_at: data.updated_at || ''
            });
            if (data.customer_name) {
              setCustomerLabel(data.customer_name);
              fetchFarmSites(data.customer_id, data.farm_site_id);
            }
          } else {
            setError('Asset not found');
          }
        } catch (err) {
          setError('Failed to load asset');
        } finally {
          setLoading(false);
        }
      };
      fetchAsset();
    }
  }, [id, isEdit, authFetch, fetchFarmSites]);

  useEffect(() => {
    const fetchAssetConfig = async () => {
      try {
        const response = await authFetch('/api/v1/assets/config');
        if (!response.ok) return;
        const data = await response.json();
        const config = normaliseAssetConfig(data.data);
        setAssetConfig(config);
      } catch (err) {
        console.error('Failed to load asset config:', err);
      }
    };

    fetchAssetConfig();
  }, [authFetch, isEdit]);

  useEffect(() => {
    if (isEdit || !initialCustomerId) return;

    const fetchInitialCustomer = async () => {
      try {
        const response = await authFetch(`/api/v1/customers/${initialCustomerId}`);
        if (!response.ok) return;
        const customer = await response.json();
        setFormData(prev => ({ ...prev, customer_id: customer.customer_id }));
        setCustomerLabel(customer.business_name || '');
        fetchFarmSites(customer.customer_id, initialFarmSiteId);
      } catch (err) {
        console.error('Failed to load initial customer:', err);
      }
    };

    fetchInitialCustomer();
  }, [authFetch, initialCustomerId, initialFarmSiteId, isEdit, fetchFarmSites]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleRobotTypeChange = (e) => {
    setFormData(prev => ({
      ...prev,
      robot_type: e.target.value
    }));
  };

  const handleCustomerSelect = (row) => {
    markDirty();
    setFormData(prev => ({ ...prev, customer_id: row ? row.customer_id : '', farm_site_id: '' }));
    setCustomerLabel(row ? row.business_name : '');
    if (row) {
      fetchFarmSites(row.customer_id);
    } else {
      setFarmSites([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    if (!formData.customer_id) {
      setError('Please select a customer');
      setSaving(false);
      return;
    }

    const isPendingAsset = isEdit && loadedStatus === 'pending_install';

    if (!formData.farm_site_id && !isPendingAsset) {
      setError('Please select a farm site');
      setSaving(false);
      return;
    }

    try {
      const url = isEdit ? `/api/v1/assets/${id}` : '/api/v1/assets';
      const method = isEdit ? 'PUT' : 'POST';

      const payload = {
        customer_id: formData.customer_id,
        farm_site_id: formData.farm_site_id || null,
        serial_number: formData.serial_number || null,
        quote_number: formData.quote_number || null,
        robot_type: formData.robot_type,
        linked_opportunity_id: formData.linked_opportunity_id || null,
        build_date: formData.build_date || null,
        install_date: formData.install_date || null,
        warranty_expiry_date: formData.warranty_expiry_date || null,
        contract_months_override: parseInt(formData.contract_months_override, 10) || null,
        brush_life_cleans: parseInt(formData.brush_life_cleans, 10) || 60000,
        spray_chemical: formData.spray_chemical || null,
        spray_dilution_ratio: formData.spray_dilution_ratio || null,
        remote_access_id: formData.remote_access_id || null,
        general_project_description: formData.general_project_description || null,
        invoicing_type: formData.invoicing_type || 'single',
        invoice_amount_minor_units: formData.invoice_amount === ''
          ? null
          : Math.round(Number(formData.invoice_amount) * 100),
        invoice_milestones: formData.invoicing_type === 'milestone'
          ? formData.invoice_milestones.map((milestone, index) => ({
            name: milestone.name || `Milestone ${index + 1}`,
            amount_minor_units: milestone.amount === '' ? 0 : Math.round(Number(milestone.amount) * 100)
          }))
          : [],
        status: formData.status || 'active',
        health_flag: formData.health_flag || 'green',
        install_notes: formData.install_notes || null,
        control_cabinet_serial: formData.control_cabinet_serial || null,
        power_cabinet_serial: formData.power_cabinet_serial || null,
        pump_cabinet_serial_1: formData.pump_cabinet_serial_1 || null,
        pump_cabinet_serial_2: formData.pump_cabinet_serial_2 || null,
        robot_serial_1: formData.robot_serial_1 || null,
        krc_cabinet_serial_1: formData.krc_cabinet_serial_1 || null,
        robot_serial_2: formData.robot_serial_2 || null,
        krc_cabinet_serial_2: formData.krc_cabinet_serial_2 || null,
        // KPP/KSP/PC/wrist/resolver/SmartPAD/Adapter 2 live in the
        // component registry (Components tab) - no longer sent from here.
        input_module_serial_1: formData.input_module_serial_1 || null,
        input_module_serial_2: formData.input_module_serial_2 || null,
        safety_module_serial: formData.safety_module_serial || null,
        output_module_serial: formData.output_module_serial || null,
        ej_coupler_serial: formData.ej_coupler_serial || null,
        ptl_pcb_serial: formData.ptl_pcb_serial || null,
        beckoff_plc_serial: formData.beckoff_plc_serial || null,
        beckoff_psu_2420_serial: formData.beckoff_psu_2420_serial || null,
        beckoff_psu_4810_serial: formData.beckoff_psu_4810_serial || null,
        beckoff_os_type: formData.beckoff_os_type || null,
        flojet_pump_serial: formData.flojet_pump_serial || null,
        kss_version_1: formData.kss_version_1 || null,
        kss_version_2: formData.kss_version_2 || null,
        plc_version: formData.plc_version || null,
        tool_camera_version: formData.tool_camera_version || null,
        cow_locate_camera_enabled: formData.cow_locate_camera_enabled,
        cow_locate_mac_id: formData.cow_locate_mac_id || null,
        pcb_type: formData.pcb_type || null,
        pin_type: formData.pin_type || null,
        air_motor_type: formData.air_motor_type || null,
        air_motor_serial_1: formData.air_motor_serial_1 || null,
        air_motor_serial_2: formData.air_motor_serial_2 || null,
        air_regulator_revision: formData.air_regulator_revision || null,
        pressure_relief_installed: formData.pressure_relief_installed,
        unifi_router_size: formData.unifi_router_size || null,
        unifi_router_mac: formData.unifi_router_mac || null,
        tsw_switch_serial: formData.tsw_switch_serial || null,
        waybill_number: formData.waybill_number || null,
        deviation_notes: formData.deviation_notes || null,
        annual_service_duration_hours: parseFloat(formData.annual_service_duration_hours) || 4,
        health_check_duration_hours: parseFloat(formData.health_check_duration_hours) || 1.5
      };

      // Optimistic lock: send back the updated_at we loaded so a concurrent
      // edit by someone else returns 409 instead of being silently overwritten
      if (isEdit && formData.updated_at) {
        payload.updated_at = formData.updated_at;
      }

      const response = await authFetch(url, {
        method,
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        navigate(`/assets/${data.asset_id}`);
      } else {
        const errorData = await response.json();
        if (response.status === 409) setStaleRecord(true);
        setError(errorData.message || 'Failed to save asset');
      }
    } catch (err) {
      setError('Failed to save asset');
    } finally {
      setSaving(false);
    }
  };

  const isBrushType = formData.robot_type.includes('brush');
  const isDualType = formData.robot_type.includes('dual');
  const isGeneralProject = formData.robot_type === 'general_project';
  const selectedAssetType = getAssetTypeMeta(formData.robot_type, assetConfig);
  const selectedSite = farmSites.find(site => site.site_id === formData.farm_site_id) || null;
  const selectedSiteAddress = selectedSite
    ? [
        selectedSite.address_line_1,
        selectedSite.address_line_2,
        selectedSite.town_city,
        selectedSite.county_region,
        selectedSite.postcode
      ].filter(Boolean).join(', ')
    : '';
  const displaySiteValue = (value) => {
    if (value === null || value === undefined || value === '') return 'Not recorded';
    return String(value).replace(/_/g, ' ');
  };
  const brushLifeDays = isBrushType && selectedSite?.cows_milking && selectedSite?.milkings_per_day
    ? Math.ceil((parseInt(formData.brush_life_cleans, 10) || 60000) / (Number(selectedSite.cows_milking) * Number(selectedSite.milkings_per_day)))
    : null;
  const setMilestoneCount = (count) => {
    const nextCount = Math.max(0, Math.min(parseInt(count, 10) || 0, 24));
    setFormData(prev => ({
      ...prev,
      invoice_milestones: Array.from({ length: nextCount }, (_, index) => (
        prev.invoice_milestones[index] || { name: `Milestone ${index + 1}`, amount: '' }
      ))
    }));
  };
  const updateMilestone = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      invoice_milestones: prev.invoice_milestones.map((milestone, idx) => (
        idx === index ? { ...milestone, [field]: value } : milestone
      ))
    }));
  };

  if (loading) {
    return <div className="loading-page">Loading...</div>;
  }

  return (
    <div className="asset-form-page">
      <div className="form-header">
        {isEdit ? (
          <Link to={`/assets/${id}`} className="back-link">← Asset</Link>
        ) : (
          <BackToList to="/assets" className="back-link">← Assets</BackToList>
        )}
        <h1>{isEdit ? 'Edit Asset' : 'New Asset'}</h1>
      </div>

      {guardElement}
      <form onChangeCapture={markDirty} onSubmit={handleSubmit} className="asset-form">
        {error && (
          <div className="form-error">
            <span>{error}</span>
            {staleRecord && (
              <button type="button" className="form-error-link" onClick={() => window.location.reload()}>
                Reload latest version
              </button>
            )}
          </div>
        )}

        {/* Asset Type Preview */}
        <div className="form-section asset-type-section">
          <div className="asset-type-header">
            <div className="asset-type-preview-large">
              <img src={resolveAssetImageSrc(selectedAssetType.image)} alt={selectedAssetType.label} />
            </div>
            <div className="asset-type-info">
              <h2>{isEdit ? formData.serial_number || 'Asset' : 'New Asset'}</h2>
              <p>{selectedAssetType.label} - {selectedAssetType.summary}</p>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="robot_type">Asset Type *</label>
                  <select
                    id="robot_type"
                    name="robot_type"
                    value={formData.robot_type}
                    onChange={handleRobotTypeChange}
                    required
                  >
                    {robotTypes.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                {isEdit && (
                  <>
                    <div className="form-group">
                      <label htmlFor="status">Status</label>
                      <select id="status" name="status" value={formData.status || 'active'} onChange={handleChange}>
                        {statusTypes.map(status => (
                          <option key={status.value} value={status.value}>{status.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="health_flag">Health</label>
                      <select id="health_flag" name="health_flag" value={formData.health_flag || 'green'} onChange={handleChange}>
                        {healthTypes.map(health => (
                          <option key={health.value} value={health.value}>{health.label}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Customer & Site */}
        <div className="form-section">
          <h2>Customer & Site</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="customer_search">Customer *</label>
              <CustomerSearchSelect
                id="customer_search"
                kind="customer"
                value={formData.customer_id}
                initialLabel={customerLabel}
                onSelect={handleCustomerSelect}
              />
            </div>

            <div className="form-group">
              <label htmlFor="farm_site_id">Farm Site *</label>
              <select
                id="farm_site_id"
                name="farm_site_id"
                value={formData.farm_site_id}
                onChange={handleChange}
                required={!(isEdit && loadedStatus === 'pending_install')}
                disabled={!formData.customer_id}
              >
                <option value="">Select farm site...</option>
                {farmSites.map(site => (
                  <option key={site.site_id} value={site.site_id}>
                    {site.site_name}{site.town_city ? ` - ${site.town_city}` : ''}{site.is_primary ? ' (Primary)' : ''}
                  </option>
                ))}
              </select>
              {formData.customer_id && farmSites.length === 0 && (
                <div className="search-status">
                  No farm sites found. <Link to={`/customers/${formData.customer_id}/sites/new`}>Add a site</Link>
                </div>
              )}
            </div>
          </div>

          {selectedSite && (
            <div className="selected-site-summary">
              <div className="site-summary-header">
                <span>Site: {selectedSite.site_name}</span>
                <Link to={`/customers/${formData.customer_id}/sites/${selectedSite.site_id}/edit`}>
                  Edit site
                </Link>
              </div>
              <div className="site-summary-grid">
                <div>
                  <span className="summary-label">Address</span>
                  <span className="summary-value">{selectedSiteAddress || 'Not recorded'}</span>
                </div>
                <div>
                  <span className="summary-label">Parlour</span>
                  <span className="summary-value">
                    {[displaySiteValue(selectedSite.parlour_type), selectedSite.parlour_size ? `${selectedSite.parlour_size} points` : null]
                      .filter(value => value && value !== 'Not recorded')
                      .join(' - ') || 'Not recorded'}
                  </span>
                </div>
                <div>
                  <span className="summary-label">Cows milking</span>
                  <span className="summary-value">{displaySiteValue(selectedSite.cows_milking)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Identification */}
        <div className="form-section">
          <h2>Identification</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="serial_number">{isGeneralProject ? 'Project Reference' : 'System Serial Number *'}</label>
              <input
                type="text"
                id="serial_number"
                name="serial_number"
                value={formData.serial_number}
                onChange={handleChange}
                required={!isGeneralProject && !(isEdit && loadedStatus === 'pending_install')}
              />
            </div>

            <div className="form-group">
              <label htmlFor="quote_number">Q-Number</label>
              <input
                type="text"
                id="quote_number"
                name="quote_number"
                value={formData.quote_number}
                onChange={handleChange}
                placeholder="e.g. Q-12345"
              />
            </div>

            <div className="form-group">
              <label htmlFor="build_date">Build Date</label>
              <input
                type="date"
                id="build_date"
                name="build_date"
                value={formData.build_date}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        {/* Installation & Service */}
        <div className="form-section">
          <h2>Installation & Service</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="install_date">Install Date *</label>
              <input
                type="date"
                id="install_date"
                name="install_date"
                value={formData.install_date}
                onChange={handleChange}
                required={!(isEdit && loadedStatus === 'pending_install')}
              />
            </div>

            <div className="form-group">
              <label htmlFor="warranty_expiry_date">Warranty Expiry</label>
              <input
                type="date"
                id="warranty_expiry_date"
                name="warranty_expiry_date"
                value={formData.warranty_expiry_date}
                onChange={handleChange}
              />
              <span className="field-help">
                Leave blank to inherit the product type's configured warranty
                from the install date (Product Configuration). An explicit
                date always wins.
              </span>
            </div>

            {selectedAssetType?.maintenance_model === 'robot_plan' && (
              <div className="form-group">
                <label htmlFor="contract_months_override">Contract Length (months)</label>
                <input
                  type="number"
                  id="contract_months_override"
                  name="contract_months_override"
                  min="3"
                  max="240"
                  value={formData.contract_months_override}
                  onChange={handleChange}
                  placeholder={selectedAssetType?.contract_months || 60}
                />
                <span className="field-help">
                  Leave blank to use the product type's configured contract
                  length. Sets how far the maintenance plan extends when the
                  schedule is generated; existing schedules are unchanged.
                </span>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="annual_service_duration_hours">Annual Service Duration (hours)</label>
              <input
                type="number"
                id="annual_service_duration_hours"
                name="annual_service_duration_hours"
                value={formData.annual_service_duration_hours}
                onChange={handleChange}
                min="0.5"
                max="24"
                step="0.5"
              />
              <span className="field-help">Duration shown on calendar (default: 4 hours)</span>
            </div>

            <div className="form-group">
              <label htmlFor="health_check_duration_hours">Quarterly Inspection Duration (hours)</label>
              <input
                type="number"
                id="health_check_duration_hours"
                name="health_check_duration_hours"
                value={formData.health_check_duration_hours}
                onChange={handleChange}
                min="0.5"
                max="24"
                step="0.5"
              />
              <span className="field-help">Duration shown on calendar (default: 1.5 hours)</span>
            </div>

            {isBrushType && (
              <>
                <div className="form-group">
                  <label htmlFor="brush_life_cleans">Brush Life (cleans)</label>
                  <input
                    type="number"
                    id="brush_life_cleans"
                    name="brush_life_cleans"
                    value={formData.brush_life_cleans}
                    onChange={handleChange}
                    min="1"
                  />
                  <span className="field-help">
                    {brushLifeDays
                      ? `~${brushLifeDays} days based on site data`
                      : 'Days calculated from site herd data'}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Chemical Configuration - for all robots */}
        {!isGeneralProject && (
          <div className="form-section">
            <h2>Chemical Configuration</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="spray_chemical">Chemical</label>
                <input
                  list="asset_spray_chemicals"
                  type="text"
                  id="spray_chemical"
                  name="spray_chemical"
                  value={formData.spray_chemical}
                  onChange={handleChange}
                />
                <datalist id="asset_spray_chemicals">
                  {(assetConfig.spray_chemicals || []).map(chemical => (
                    <option key={chemical} value={chemical} />
                  ))}
                </datalist>
              </div>

              <div className="form-group">
                <label htmlFor="spray_dilution_ratio">Dilution Ratio</label>
                <input
                  type="text"
                  id="spray_dilution_ratio"
                  name="spray_dilution_ratio"
                  value={formData.spray_dilution_ratio}
                  onChange={handleChange}
                  placeholder="e.g., 1:50"
                />
              </div>
            </div>
          </div>
        )}

        {/* Connectivity - for all robots */}
        {!isGeneralProject && (
          <div className="form-section">
            <h2>Connectivity</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="remote_access_id">Remote Access Type</label>
                <select
                  id="remote_access_id"
                  name="remote_access_id"
                  value={formData.remote_access_id}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  <option value="TeamViewer">TeamViewer</option>
                  <option value="Tailscale">Tailscale</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* General Project */}
        {isGeneralProject && (
          <div className="form-section">
            <h2>Project Details</h2>
            <div className="form-grid">
              <div className="form-group full-width">
                <label htmlFor="general_project_description">Description</label>
                <textarea
                  id="general_project_description"
                  name="general_project_description"
                  value={formData.general_project_description}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Describe the project..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="invoicing_type">Invoicing</label>
                <select
                  id="invoicing_type"
                  name="invoicing_type"
                  value={formData.invoicing_type}
                  onChange={handleChange}
                >
                  <option value="single">Single amount</option>
                  <option value="milestone">Milestone based</option>
                </select>
              </div>

              {formData.invoicing_type === 'single' ? (
                <div className="form-group">
                  <label htmlFor="invoice_amount">Invoice Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    id="invoice_amount"
                    name="invoice_amount"
                    value={formData.invoice_amount}
                    onChange={handleChange}
                    placeholder="0.00"
                  />
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label htmlFor="milestone_count">Milestones</label>
                    <input
                      type="number"
                      min="0"
                      max="24"
                      id="milestone_count"
                      value={formData.invoice_milestones.length}
                      onChange={(e) => setMilestoneCount(e.target.value)}
                    />
                  </div>
                  <div className="form-group full-width milestone-grid">
                    {formData.invoice_milestones.map((milestone, index) => (
                      <div key={index} className="milestone-row">
                        <input
                          type="text"
                          value={milestone.name}
                          onChange={(e) => updateMilestone(index, 'name', e.target.value)}
                          placeholder={`Milestone ${index + 1}`}
                        />
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={milestone.amount}
                          onChange={(e) => updateMilestone(index, 'amount', e.target.value)}
                          placeholder="0.00"
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Robot Equipment Sections - Collapsible */}
        {!isGeneralProject && (
          <>
            {/* Equipment Serials */}
            <div className={`form-section collapsible ${expandedSections.equipment ? 'expanded' : ''}`}>
              <button type="button" className="section-toggle" onClick={() => toggleSection('equipment')}>
                <h2>Equipment Serials</h2>
                <span className="toggle-hint">{expandedSections.equipment ? 'Hide' : 'Show'} cabinet, robot, and module serials</span>
                <span className="toggle-icon">{expandedSections.equipment ? '−' : '+'}</span>
              </button>

              {expandedSections.equipment && (
                <div className="section-content">
                  <h3 className="subsection-title">Cabinet Serials</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="control_cabinet_serial">Control Cabinet Ser No.</label>
                      <input type="text" id="control_cabinet_serial" name="control_cabinet_serial" value={formData.control_cabinet_serial} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="power_cabinet_serial">Power Cabinet Ser No.</label>
                      <input type="text" id="power_cabinet_serial" name="power_cabinet_serial" value={formData.power_cabinet_serial} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="pump_cabinet_serial_1">Pump Cabinet Ser No.</label>
                      <input type="text" id="pump_cabinet_serial_1" name="pump_cabinet_serial_1" value={formData.pump_cabinet_serial_1} onChange={handleChange} />
                    </div>
                    {isDualType && (
                      <div className="form-group">
                        <label htmlFor="pump_cabinet_serial_2">Pump Cabinet 2 Ser No.</label>
                        <input type="text" id="pump_cabinet_serial_2" name="pump_cabinet_serial_2" value={formData.pump_cabinet_serial_2} onChange={handleChange} />
                      </div>
                    )}
                  </div>

                  <h3 className="subsection-title">KUKA Robot {isDualType ? '1' : ''}</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="robot_serial_1">Robot Ser No.</label>
                      <input type="text" id="robot_serial_1" name="robot_serial_1" value={formData.robot_serial_1} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="krc_cabinet_serial_1">KRC Cabinet Ser No.</label>
                      <input type="text" id="krc_cabinet_serial_1" name="krc_cabinet_serial_1" value={formData.krc_cabinet_serial_1} onChange={handleChange} />
                    </div>
                  </div>
                  {/* KPP/KSP/PC/wrist/resolver/SmartPAD are tracked parts now —
                      register and move them on the asset's Components tab. */}
                  <p className="form-hint">
                    KPP, KSP, PC, Robot Wrist, Resolver and SmartPAD serials are recorded on the
                    asset&apos;s <strong>Components</strong> tab, where each part keeps its movement history.
                  </p>

                  {isDualType && (
                    <>
                      <h3 className="subsection-title">KUKA Robot 2</h3>
                      <div className="form-grid">
                        <div className="form-group">
                          <label htmlFor="robot_serial_2">Robot 2 Ser No.</label>
                          <input type="text" id="robot_serial_2" name="robot_serial_2" value={formData.robot_serial_2} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                          <label htmlFor="krc_cabinet_serial_2">KRC 2 Cabinet Ser No.</label>
                          <input type="text" id="krc_cabinet_serial_2" name="krc_cabinet_serial_2" value={formData.krc_cabinet_serial_2} onChange={handleChange} />
                        </div>
                      </div>
                    </>
                  )}

                  <h3 className="subsection-title">Beckoff Parts</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="beckoff_plc_serial">PLC Serial</label>
                      <input type="text" id="beckoff_plc_serial" name="beckoff_plc_serial" value={formData.beckoff_plc_serial} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="input_module_serial_1">DI 1809</label>
                      <input type="text" id="input_module_serial_1" name="input_module_serial_1" value={formData.input_module_serial_1} onChange={handleChange} />
                    </div>
                    {isDualType && (
                      <div className="form-group">
                        <label htmlFor="input_module_serial_2">DI 1809 2</label>
                        <input type="text" id="input_module_serial_2" name="input_module_serial_2" value={formData.input_module_serial_2} onChange={handleChange} />
                      </div>
                    )}
                    <div className="form-group">
                      <label htmlFor="safety_module_serial">Safety Module 1957</label>
                      <input type="text" id="safety_module_serial" name="safety_module_serial" value={formData.safety_module_serial} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="output_module_serial">Output Module 2809</label>
                      <input type="text" id="output_module_serial" name="output_module_serial" value={formData.output_module_serial} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="ej_coupler_serial">EJ Coupler</label>
                      <input type="text" id="ej_coupler_serial" name="ej_coupler_serial" value={formData.ej_coupler_serial} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="ptl_pcb_serial">PTL PCB</label>
                      <input type="text" id="ptl_pcb_serial" name="ptl_pcb_serial" value={formData.ptl_pcb_serial} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="beckoff_psu_2420_serial">PSU 2420</label>
                      <input type="text" id="beckoff_psu_2420_serial" name="beckoff_psu_2420_serial" value={formData.beckoff_psu_2420_serial} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="beckoff_psu_4810_serial">PSU 4810</label>
                      <input type="text" id="beckoff_psu_4810_serial" name="beckoff_psu_4810_serial" value={formData.beckoff_psu_4810_serial} onChange={handleChange} />
                    </div>
                  </div>

                  <h3 className="subsection-title">Pump Cabinet</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="flojet_pump_serial">FloJet Pump</label>
                      <input type="text" id="flojet_pump_serial" name="flojet_pump_serial" value={formData.flojet_pump_serial} onChange={handleChange} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Software & Versions */}
            <div className={`form-section collapsible ${expandedSections.software ? 'expanded' : ''}`}>
              <button type="button" className="section-toggle" onClick={() => toggleSection('software')}>
                <h2>Software & Versions</h2>
                <span className="toggle-hint">{expandedSections.software ? 'Hide' : 'Show'} KSS, PLC, and camera versions</span>
                <span className="toggle-icon">{expandedSections.software ? '−' : '+'}</span>
              </button>

              {expandedSections.software && (
                <div className="section-content">
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="kss_version_1">{isDualType ? 'Robot 1 KSS Version' : 'KSS Version'}</label>
                      <input type="text" id="kss_version_1" name="kss_version_1" value={formData.kss_version_1} onChange={handleChange} placeholder="e.g., 8.6.12" />
                    </div>
                    {isDualType && (
                      <div className="form-group">
                        <label htmlFor="kss_version_2">Robot 2 KSS Version</label>
                        <input type="text" id="kss_version_2" name="kss_version_2" value={formData.kss_version_2} onChange={handleChange} placeholder="e.g., 8.6.12" />
                      </div>
                    )}
                    <div className="form-group">
                      <label htmlFor="plc_version">PLC SW Ver</label>
                      <input type="text" id="plc_version" name="plc_version" value={formData.plc_version} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="tool_camera_version">Tool Camera SW Ver</label>
                      <input type="text" id="tool_camera_version" name="tool_camera_version" value={formData.tool_camera_version} onChange={handleChange} />
                    </div>
                  </div>

                  <h3 className="subsection-title">Cow Locate Camera</h3>
                  <div className="form-grid">
                    <div className="form-group checkbox-inline">
                      <label>
                        <input type="checkbox" name="cow_locate_camera_enabled" checked={formData.cow_locate_camera_enabled} onChange={handleChange} />
                        Cow Locate Camera Installed
                      </label>
                    </div>
                    {formData.cow_locate_camera_enabled && (
                      <div className="form-group">
                        <label htmlFor="cow_locate_mac_id">MAC ID</label>
                        <input type="text" id="cow_locate_mac_id" name="cow_locate_mac_id" value={formData.cow_locate_mac_id} onChange={handleChange} placeholder="00:02:01:50:XX:XX" />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Hardware Configuration */}
            <div className={`form-section collapsible ${expandedSections.hardware ? 'expanded' : ''}`}>
              <button type="button" className="section-toggle" onClick={() => toggleSection('hardware')}>
                <h2>Hardware Configuration</h2>
                <span className="toggle-hint">{expandedSections.hardware ? 'Hide' : 'Show'} PCB, Beckoff, and network settings</span>
                <span className="toggle-icon">{expandedSections.hardware ? '−' : '+'}</span>
              </button>

              {expandedSections.hardware && (
                <div className="section-content">
                  <h3 className="subsection-title">Other Components</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="pcb_type">PCB Type</label>
                      <select id="pcb_type" name="pcb_type" value={formData.pcb_type} onChange={handleChange}>
                        <option value="">Select...</option>
                        <option value="EL">EL</option>
                        <option value="EJ Molex">EJ Molex</option>
                        <option value="EJ PushFit">EJ PushFit</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="pin_type">Pin Type</label>
                      <select id="pin_type" name="pin_type" value={formData.pin_type} onChange={handleChange}>
                        <option value="">Select...</option>
                        <option value="Single Pin">Single Pin</option>
                        <option value="Double Pinned">Double Pinned</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="air_motor_type">Air Motor</label>
                      <select id="air_motor_type" name="air_motor_type" value={formData.air_motor_type} onChange={handleChange}>
                        <option value="">Select...</option>
                        <option value="Low Torque">Low Torque</option>
                        <option value="High Torque">High Torque</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="air_motor_serial_1">{isDualType ? 'Robot 1 Air Motor Serial' : 'Air Motor Serial'}</label>
                      <input type="text" id="air_motor_serial_1" name="air_motor_serial_1" value={formData.air_motor_serial_1} onChange={handleChange} />
                    </div>
                    {isDualType && (
                      <div className="form-group">
                        <label htmlFor="air_motor_serial_2">Robot 2 Air Motor Serial</label>
                        <input type="text" id="air_motor_serial_2" name="air_motor_serial_2" value={formData.air_motor_serial_2} onChange={handleChange} />
                      </div>
                    )}
                    <div className="form-group">
                      <label htmlFor="air_regulator_revision">Air Regulator Rev</label>
                      <select id="air_regulator_revision" name="air_regulator_revision" value={formData.air_regulator_revision} onChange={handleChange}>
                        <option value="">Select...</option>
                        <option value="Rev A">Rev A</option>
                        <option value="Rev B">Rev B</option>
                      </select>
                    </div>
                    <div className="form-group checkbox-inline">
                      <label>
                        <input type="checkbox" name="pressure_relief_installed" checked={formData.pressure_relief_installed} onChange={handleChange} />
                        Pressure Relief Installed
                      </label>
                    </div>
                  </div>

                  <h3 className="subsection-title">Beckoff</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="beckoff_os_type">Windows Version</label>
                      <select id="beckoff_os_type" name="beckoff_os_type" value={formData.beckoff_os_type} onChange={handleChange}>
                        <option value="">Select...</option>
                        <option value="Windows CE">Windows CE</option>
                        <option value="Windows 10">Windows 10</option>
                        <option value="Windows 11">Windows 11</option>
                      </select>
                    </div>
                  </div>

                  <h3 className="subsection-title">Network Hardware</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="unifi_router_size">UniFi Router</label>
                      <select id="unifi_router_size" name="unifi_router_size" value={formData.unifi_router_size} onChange={handleChange}>
                        <option value="">Select...</option>
                        <option value="512 MB">512 MB</option>
                        <option value="1 GB">1 GB</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="unifi_router_mac">UniFi MAC</label>
                      <input type="text" id="unifi_router_mac" name="unifi_router_mac" value={formData.unifi_router_mac} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="tsw_switch_serial">TSW Switch</label>
                      <input type="text" id="tsw_switch_serial" name="tsw_switch_serial" value={formData.tsw_switch_serial} onChange={handleChange} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Quality & Tracking */}
            <div className={`form-section collapsible ${expandedSections.quality ? 'expanded' : ''}`}>
              <button type="button" className="section-toggle" onClick={() => toggleSection('quality')}>
                <h2>Quality & Tracking</h2>
                <span className="toggle-hint">{expandedSections.quality ? 'Hide' : 'Show'} waybill and deviations</span>
                <span className="toggle-icon">{expandedSections.quality ? '−' : '+'}</span>
              </button>

              {expandedSections.quality && (
                <div className="section-content">
                  <h3 className="subsection-title">Swap / Replacement</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="waybill_number">Waybill Number</label>
                      <input type="text" id="waybill_number" name="waybill_number" value={formData.waybill_number} onChange={handleChange} />
                    </div>
                  </div>

                  <h3 className="subsection-title">Deviations / Upgrades</h3>
                  <div className="form-group full-width">
                    <textarea
                      id="deviation_notes"
                      name="deviation_notes"
                      value={formData.deviation_notes}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Note any deviations or required upgrades..."
                    />
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Notes */}
        <div className="form-section">
          <h2>Notes</h2>
          <div className="form-group full-width">
            <textarea
              id="install_notes"
              name="install_notes"
              value={formData.install_notes}
              onChange={handleChange}
              rows={4}
              placeholder="Installation notes, special considerations..."
            />
          </div>
        </div>

        {/* Actions */}
        <div className="form-actions">
          {isEdit ? (
            <Link to={`/assets/${id}`} className="btn-cancel">
              Cancel
            </Link>
          ) : (
            <BackToList to="/assets" className="btn-cancel">
              Cancel
            </BackToList>
          )}
          <button type="submit" className="btn-save" disabled={saving}>
            {saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Asset')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AssetForm;
